import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout'
import { AfterViewInit, ChangeDetectorRef, Component, ViewChild } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { GoogleAnalyticsService } from 'ngx-google-analytics'
import { BehaviorSubject, fromEvent, of, ReplaySubject } from 'rxjs'
import { concatMap, debounceTime, distinct, filter, last, map, take, tap } from 'rxjs/operators'
import { BoundingBox, Criteria, SearchListing, SearchListingsGQL } from '../../../graphql'
import { breakpoints, searchListingsCriteriaKey } from '../../constant'
import { TravelAgentService } from '../../injectable/travel-agent-service'
import { UnsubscribeOnDestroy, scrollToTop } from '../../utility'
import { WhiteLabelService } from '../account/white-label-settings/white-label.service'
import { SearchCriteriaComponent } from './search-criteria/search-criteria'
import { SearchFiltersComponent } from './search-criteria/search-filters/search-filters'
import { MapBounds, SearchMapComponent } from './search-criteria/search-map/search-map'
import { MatSnackBar } from '@angular/material/snack-bar'
import { SearchMetadata } from './search-metadata'
import { ConsumerViewService } from '../../injectable/consumer-view-service'
import { GoogleMapsService } from '../../injectable/google-maps-service'

@Component({
    selector: 'app-search-listings',
    styleUrls: ['search-listings.scss'],
    templateUrl: 'search-listings.html',
})
export class SearchListingsComponent extends UnsubscribeOnDestroy implements AfterViewInit {
    mapInfo$: BehaviorSubject<MapBounds> = new BehaviorSubject<MapBounds>({
        bounds: {
            swLng: 0,
            swLat: 0,
            neLng: 0,
            neLat: 0,
        },
        center: {
            lat: 0,
            lng: 0,
            zoom: 0,
        }
    })

    options: any
    checkin: string
    checkout: string

    /**
     * When user is searching without travel dates, the
     * "Available from <date>" is shown. The date shown depends
     * on the size of consecutive nights required.
     */
    minimumConsecutiveNights = 7
    travelAgentView = true
    criteria: Criteria
    count: number
    listingsOnMapCount: number
    totalGuests = 0
    loading = true
    searching = false
    searchMetadata$ = new ReplaySubject<SearchMetadata>(1)
    loadingTimeout: ReturnType<typeof setTimeout>
    listings$: BehaviorSubject<Array<SearchListing>> = new BehaviorSubject([])
    p = 1
    pTotal: number
    total: number
    hoveredListingId$ = new BehaviorSubject<string>('')
    mobileView: boolean
    isWhiteLabel: boolean;
    mobileViewActive: 'map' | 'listings' = 'map'
    showMobileSearchCriteria = false
    activeSearchCriteria: string | undefined
    showMobileSearchFilters = false
    searchListings: SearchListing[]
    selectedFiltersCount = 0
    searchRegionName: string | undefined
    listingsScrolled = false
    showMobileSearchByText = false
    initMapBoundingBox: string
    consumerView = false
    initWithAiSearch: string | undefined
    showInquiryBanner = false
    scrollToTop = scrollToTop
    aiSearchMode = false
    aiSearchResults = false
    aiSearchText: string
    aiSearchTimer: ReturnType<typeof setTimeout>
    aiSearchTime = 10000
    aiSearchMapBounds: MapBounds | undefined

    @ViewChild(SearchMapComponent) searchMap
    @ViewChild(SearchFiltersComponent) searchFilters!: SearchFiltersComponent
    @ViewChild(SearchCriteriaComponent) searchCriteria!: SearchCriteriaComponent

    constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private _searchListingsGQL: SearchListingsGQL,
        private _gaService: GoogleAnalyticsService,
        private _travelAgentService: TravelAgentService,
        private _whiteLabelService: WhiteLabelService,
        private _breakpointObserver: BreakpointObserver,
        private _activatedRoute: ActivatedRoute,
        private _snackBar: MatSnackBar,
        private _consumerViewService: ConsumerViewService,
        private _googleMapsService: GoogleMapsService
    ) {
        super()

        this.isWhiteLabel = this._whiteLabelService.isWhiteLabelEnabled()
        this._consumerViewService.consumerViewSubject.subscribe(consumerView => {
            this.consumerView = consumerView || this.isWhiteLabel
        })

        this.initWithAiSearch = this._activatedRoute.snapshot.queryParams.aiSearchText
        this.initMapBoundingBox = this._activatedRoute.snapshot.queryParams.boundingBox

        if (this.initMapBoundingBox) {
            this.initMapBoundingBox = JSON.parse(this.initMapBoundingBox)
        }

        // clear query params from url
        window.history.replaceState(null, '', window.location.pathname)

        this._breakpointObserver.observe([
            `(max-width: ${breakpoints.smMax})`
        ]).subscribe((result: BreakpointState) => {
            this.mobileView = result.matches
            this.aiSearchMode = false
        })

        fromEvent(window, 'scroll')
            .pipe(debounceTime(100))
            .subscribe(() => {
                this.listingsScrolled = window.scrollY > 500
            })

        this.showHideInquiryBanner()
    }

    ngAfterViewInit(): void {
        this.totalGuests = this.getTotaleGuests()
        this.determineViewType()

        const sub = this.mapInfo$.pipe(
            debounceTime(500),
            distinct()
        ).subscribe(_ => {
            if (!this?.criteria) { return }
            this.criteria.paging.offset = 0
            this.p = 1 // reset pagination when moving map

            if (this.initWithAiSearch) {
                this.criteria.searchText = this.initWithAiSearch
                this.aiSearchMode = true
            }

            this.search(this.criteria)          
        })

        this._subscriptions.push(sub)
    }

    showHideInquiryBanner() {
        const time = 10000
        setTimeout(() => {
            this.showInquiryBanner = true
            setTimeout(() => {
                this.showInquiryBanner = false
            }, time)
        }, time)
    }

    toggleAiSearch($event: boolean) {
        this.aiSearchMode = $event
    }

    aiSearch($event: string) {
        this.aiSearchText = $event
        this.criteria.searchText = this.aiSearchMode ? $event : undefined
        this.search(this.criteria)

        if ($event) {
            this.showView('listings')
        }
    }

    // Public
    search(criteria: Criteria) {
        this.setCriteria(criteria)

        this.criteria.locationCriterion = {
            swLat: this.mapInfo$.value.bounds.swLat,
            swLng: this.mapInfo$.value.bounds.swLng,
            neLat: this.mapInfo$.value.bounds.neLat,
            neLng: this.mapInfo$.value.bounds.neLng
        }

        // when exiting AI search use current stored map bounds 
        // to perform standard search.
        if (this.aiSearchMapBounds) {
            this.resetSelectedRegion()
            const bounds = this.aiSearchMapBounds
            this.aiSearchMode = false   
            this.aiSearchMapBounds = undefined
            this.updateBounds(bounds, true)
            return
        }

        this.getPage((criteria.paging.offset / criteria.paging.size) + 1)
    }

    setCriteria(criteria: Criteria) {
        this.criteria = this.criteria ? Object.assign(this.criteria, criteria) : criteria
    }

    getPage(page: number) {
        this._googleMapsService.markers = []
        scrollToTop()
        this.searching = true
        this.aiSearchResults = false
        this.criteria.paging.offset = (page - 1) * this.criteria.paging.size
        this.checkin = this.criteria.datesCriterion?.checkin
        this.checkout = this.criteria.datesCriterion?.checkout
        this._gaService.event('Search Completed', 'engagement')
        this.totalGuests = this.getTotaleGuests()

        if (this.initWithAiSearch) {
            this.initWithAiSearch = undefined
        } else {
            this.changeDetector.detectChanges()
        }

        this.aiSearchTimeoutAlert()

        console.log('Searching assets with filter:', this.criteria)

        this._searchListingsGQL.fetch({ input: this.criteria })
        .pipe(
            filter(result => !result.loading),
            tap(result => {
                if (result.errors?.length) {    
                    throw new Error(`Failed to fetch search results: ${result.errors[0].message}`)
                }
                this.count = result.data.searchListings.count
                this.total = this.count

                const paging = result.data.searchListings.paging
                this.searchMetadata$.next({
                    searchId: result.data.searchListings.searchId,
                    pageRank: (listingId: string) => result.data.searchListings.results.findIndex(listing => listing.listingId === listingId),
                    pageNumber: paging.pageNumber,
                    totalResults: result.data.searchListings.count
                })

                console.log('Search ', result.data.searchListings.searchId , ' results:', result.data.searchListings.results)
                sessionStorage.setItem(searchListingsCriteriaKey, JSON.stringify(this.criteria))
            }),
            map(result => result.data.searchListings.results)
        ).subscribe(result => {

            clearTimeout(this.aiSearchTimer)
            this.p = page
            this.pTotal = Math.ceil(this.total/this.criteria.paging.size)
            this.listings$.next(result as SearchListing[])
            clearTimeout(this.loadingTimeout)

            this.loadingTimeout = setTimeout(() => {
                this.loading = false
                this.searching = false
                
                if (this.aiSearchMode) { 
                    // zoom out and center map
                    this.aiSearchResults = true
                    this.searchMap.panToCoords(0, 25, 1)
                }

                this.changeDetector.detectChanges()
            }, 100)
        })
    }

    aiSearchTimeoutAlert() {
        // AI search seems to be timing out quite often and no user feedback is given.
        // Use timer to display user message after x time that something has gone wrong.
        if (this.aiSearchMode) { 
            this.aiSearchTimer = setTimeout(() => {
                this._snackBar.open('Something went wrong with your AI search. Please try again.')
                const closeAiSearchBtn = document.getElementById('closeAiSearchInput')
                if (closeAiSearchBtn) {
                    closeAiSearchBtn.click()
                }
            }, this.aiSearchTime)
        }
    }

    updateListingCount($event: number) {
        this.listingsOnMapCount = $event
    }

    clearAllFilters() {
        this.searchFilters.clearAllFilters()
        this.searchFilters.clearAllExtendedFilters()
    }

    resetSearchCriteria() {
        this.searchRegionName = undefined
        this.searchCriteria.resetSearchCriteria()
    }

    showView(view: 'map' | 'listings'): void {
        this.mobileViewActive = view
    }

    private determineViewType() {
        this._travelAgentService
            .refreshTravelAgent()
            .pipe(
                last(),
                concatMap(() => this._travelAgentService.travelAgentSubject),
                take(1),
            )
            .subscribe(travelAgent => {
                this.travelAgentView = true
            })
    }

    updateBounds($event: MapBounds, resetRegion:boolean = false) {
        this._googleMapsService.markers = []
        if (this.aiSearchMode) { 
            // store map bounds for when exiting AI search.
            // Do not use map to init new search.
            this.aiSearchMapBounds = $event
            return 
        }

        this.mapInfo$.next($event)
        // Reset region drop down and regionCriterion when panning
        if (!this.searchCriteria.triggeredRegionUpdate) {
            this.resetSelectedRegion()
        }
    }

    resetSelectedRegion() {
        this.criteria.regionCriterion = undefined
        this.searchRegionName = undefined
        this.searchCriteria.resetSearchRegionMenu()
    }

    requestPanning($event: BoundingBox) {
        this.searchMap.panTo($event)
    }

    focusListing(id: string) {
        this.hoveredListingId$.next(id)
        this.changeDetector.detectChanges()
    }

    openMobileSearchCriteriaDrawer(activeSearchCriteria: string) {
        this.showMobileSearchCriteria = true
        this.activeSearchCriteria = activeSearchCriteria
    }

    closeMobileSearchCriteriaDrawer() {
        this.showMobileSearchCriteria = false
        this.activeSearchCriteria = undefined
    }

    openMobileSearchFiltersDrawer() {
        this.showMobileSearchFilters = true
    }

    closeMobileSearchFiltersDrawer() {
        this.showMobileSearchFilters = false
    }

    getTotaleGuests() {
        const adults = this.criteria?.guestCriterion?.adults || 0
        const children = this.criteria?.guestCriterion?.children || 0
        const infants = this.criteria?.guestCriterion?.infants || 0
        return adults + children + infants
    }

    updateSelectedFiltersCount(count: number) {
        this.selectedFiltersCount = count
    }

    updateSearchRegionName(name: string) {
        this.searchRegionName = name
    }
}

export class SearchResultsFilter {
    criteria: Criteria
}
